function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
function _object_without_properties(source, excluded) {
    if (source == null) return {};
    var target = {}, sourceKeys, key, i;
    if (typeof Reflect !== "undefined" && Reflect.ownKeys) {
        sourceKeys = Reflect.ownKeys(source);
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
        return target;
    }
    target = _object_without_properties_loose(source, excluded);
    if (Object.getOwnPropertySymbols) {
        sourceKeys = Object.getOwnPropertySymbols(source);
        for(i = 0; i < sourceKeys.length; i++){
            key = sourceKeys[i];
            if (excluded.indexOf(key) >= 0) continue;
            if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
            target[key] = source[key];
        }
    }
    return target;
}
function _object_without_properties_loose(source, excluded) {
    if (source == null) return {};
    var target = {}, sourceKeys = Object.getOwnPropertyNames(source), key, i;
    for(i = 0; i < sourceKeys.length; i++){
        key = sourceKeys[i];
        if (excluded.indexOf(key) >= 0) continue;
        if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
        target[key] = source[key];
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Slot } from '@radix-ui/react-slot';
import { Button } from '@repo/ui-primitives/button';
import { Input } from '@repo/ui-primitives/input';
import { Separator } from '@repo/ui-primitives/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui-primitives/sheet';
import { Skeleton } from '@repo/ui-primitives/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui-primitives/tooltip';
import { cva } from 'class-variance-authority';
import { PanelLeftIcon } from 'lucide-react';
import * as React from 'react';
import { useIsMobile } from '../../hooks/use-mobile';
import { cn } from '../../lib/utils';
const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
const SidebarContext = /*#__PURE__*/ React.createContext(null);
function useSidebar() {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider.');
    }
    return context;
}
function SidebarProvider(_0) {
    let { defaultOpen = true, open: openProp, onOpenChange: setOpenProp, className, style, children } = _0, props = _object_without_properties(_0, [
        "defaultOpen",
        "open",
        "onOpenChange",
        "className",
        "style",
        "children"
    ]);
    const isMobile = useIsMobile();
    const [openMobile, setOpenMobile] = React.useState(false);
    // This is the internal state of the sidebar.
    // We use openProp and setOpenProp for control from outside the component.
    const [_open, _setOpen] = React.useState(defaultOpen);
    const open = openProp !== null && openProp !== void 0 ? openProp : _open;
    const setOpen = React.useCallback((value)=>{
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
            setOpenProp(openState);
        } else {
            _setOpen(openState);
        }
        // This sets the cookie to keep the sidebar state.
        document.cookie = "".concat(SIDEBAR_COOKIE_NAME, "=").concat(openState.toString(), "; path=/; max-age=").concat(SIDEBAR_COOKIE_MAX_AGE.toString());
    }, [
        setOpenProp,
        open
    ]);
    // Helper to toggle the sidebar.
    const toggleSidebar = React.useCallback(()=>{
        if (isMobile) {
            setOpenMobile((open)=>!open);
            return;
        }
        setOpen((open)=>!open);
    }, [
        isMobile,
        setOpen,
        setOpenMobile
    ]);
    // Adds a keyboard shortcut to toggle the sidebar.
    React.useEffect(()=>{
        const handleKeyDown = (event)=>{
            if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
                event.preventDefault();
                toggleSidebar();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return ()=>{
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        toggleSidebar
    ]);
    // We add a state so that we can do data-state="expanded" or "collapsed".
    // This makes it easier to style the sidebar with Tailwind classes.
    const state = open ? 'expanded' : 'collapsed';
    const contextValue = React.useMemo(()=>({
            state,
            open,
            setOpen,
            isMobile,
            openMobile,
            setOpenMobile,
            toggleSidebar
        }), [
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar
    ]);
    return /*#__PURE__*/ _jsx(SidebarContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ _jsx(TooltipProvider, {
            delayDuration: 0,
            children: /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({
                "data-slot": "sidebar-wrapper",
                style: _object_spread({
                    '--sidebar-width': SIDEBAR_WIDTH,
                    '--sidebar-width-icon': SIDEBAR_WIDTH_ICON
                }, style),
                className: cn('group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex  h-full w-full', className)
            }, props), {
                children: children
            }))
        })
    });
}
function Sidebar(_0) {
    let { side = 'left', variant = 'sidebar', collapsible = 'offcanvas', className, children } = _0, props = _object_without_properties(_0, [
        "side",
        "variant",
        "collapsible",
        "className",
        "children"
    ]);
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
    if (collapsible === 'none') {
        return /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({
            "data-slot": "sidebar",
            className: cn('bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col', className)
        }, props), {
            children: children
        }));
    }
    if (isMobile) {
        return /*#__PURE__*/ _jsx(Sheet, _object_spread_props(_object_spread({
            open: openMobile,
            onOpenChange: setOpenMobile
        }, props), {
            children: /*#__PURE__*/ _jsxs(SheetContent, {
                "data-sidebar": "sidebar",
                "data-slot": "sidebar",
                "data-mobile": "true",
                className: "bg-sidebar text-sidebar-foreground w-(--sidebar-width) p-0 [&>button]:hidden",
                style: {
                    '--sidebar-width': SIDEBAR_WIDTH_MOBILE
                },
                side: side,
                children: [
                    /*#__PURE__*/ _jsxs(SheetHeader, {
                        className: "sr-only",
                        children: [
                            /*#__PURE__*/ _jsx(SheetTitle, {
                                children: "Sidebar"
                            }),
                            /*#__PURE__*/ _jsx(SheetDescription, {
                                children: "Displays the mobile sidebar."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex h-full w-full flex-col",
                        children: children
                    })
                ]
            })
        }));
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "group peer text-sidebar-foreground hidden md:block",
        "data-state": state,
        "data-collapsible": state === 'collapsed' ? collapsible : '',
        "data-variant": variant,
        "data-side": side,
        "data-slot": "sidebar",
        children: [
            /*#__PURE__*/ _jsx("div", {
                "data-slot": "sidebar-gap",
                className: cn('relative w-(--sidebar-width) bg-transparent transition-[width] duration-200 ease-linear', 'group-data-[collapsible=offcanvas]:w-0', 'group-data-[side=right]:rotate-180', variant === 'floating' || variant === 'inset' ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]' : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)')
            }),
            /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({
                "data-slot": "sidebar-container",
                className: cn('fixed z-10 hidden w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex border-sidebar-border', // 'relative inset-y-0 z-10 hidden h-full w-(--sidebar-width) transition-[left,right,width] duration-200 ease-linear md:flex border-[var(--border)] flex-shrink-0',
                side === 'left' ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]' : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]', // Adjust the padding for floating and inset variants.
                variant === 'floating' || variant === 'inset' ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]' : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l', className),
                style: {
                    top: 'var(--header-height)',
                    bottom: '0px'
                }
            }, props), {
                children: /*#__PURE__*/ _jsx("div", {
                    "data-sidebar": "sidebar",
                    "data-slot": "sidebar-inner",
                    className: "bg-sidebar group-data-[variant=floating]:border-sidebar-border flex h-full w-full flex-col group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow-sm",
                    children: children
                })
            }))
        ]
    });
}
function SidebarTrigger(_0) {
    let { className, onClick } = _0, props = _object_without_properties(_0, [
        "className",
        "onClick"
    ]);
    const { toggleSidebar } = useSidebar();
    return /*#__PURE__*/ _jsxs(Button, _object_spread_props(_object_spread({
        "data-sidebar": "trigger",
        "data-slot": "sidebar-trigger",
        variant: "ghost",
        size: "icon",
        className: cn('size-7 cursor-pointer', className),
        onClick: (event)=>{
            onClick === null || onClick === void 0 ? void 0 : onClick(event);
            toggleSidebar();
        }
    }, props), {
        children: [
            /*#__PURE__*/ _jsx(PanelLeftIcon, {
                className: "text-white"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "sr-only",
                children: "Toggle Sidebar"
            })
        ]
    }));
}
function SidebarRail(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    const { toggleSidebar } = useSidebar();
    return /*#__PURE__*/ _jsx("button", _object_spread({
        "data-sidebar": "rail",
        "data-slot": "sidebar-rail",
        "aria-label": "Toggle Sidebar",
        tabIndex: -1,
        onClick: toggleSidebar,
        title: "Toggle Sidebar",
        className: cn('hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex', 'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize', '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize', 'hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full', '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2', '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2', className)
    }, props));
}
function SidebarInset(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("main", _object_spread({
        "data-slot": "sidebar-inset",
        className: cn('bg-background relative flex w-full flex-1 flex-col', 'md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2', className)
    }, props));
}
function SidebarInput(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx(Input, _object_spread({
        "data-slot": "sidebar-input",
        "data-sidebar": "input",
        className: cn('bg-background h-8 w-full shadow-none', className)
    }, props));
}
function SidebarHeader(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        "data-slot": "sidebar-header",
        "data-sidebar": "header",
        className: cn('flex flex-col gap-2 p-2', className)
    }, props));
}
function SidebarFooter(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        "data-slot": "sidebar-footer",
        "data-sidebar": "footer",
        className: cn('flex flex-col gap-2 p-2', className)
    }, props));
}
function SidebarSeparator(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx(Separator, _object_spread({
        "data-slot": "sidebar-separator",
        "data-sidebar": "separator",
        className: cn('bg-sidebar-border mx-2 w-auto', className)
    }, props));
}
function SidebarContent(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        "data-slot": "sidebar-content",
        "data-sidebar": "content",
        className: cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden', // Custom scrollbar styling
        'scrollbar-gutter-stable', '[&::-webkit-scrollbar]:w-2', '[&::-webkit-scrollbar-track]:bg-transparent', '[&::-webkit-scrollbar-thumb]:bg-muted-foreground/80', '[&::-webkit-scrollbar-thumb]:rounded-full', '[&::-webkit-scrollbar-thumb]:border-2', '[&::-webkit-scrollbar-thumb]:border-solid', '[&::-webkit-scrollbar-thumb]:border-transparent', 'hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground', className)
    }, props));
}
function SidebarGroup(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        "data-slot": "sidebar-group",
        "data-sidebar": "group",
        className: cn('relative flex w-full min-w-0 flex-col p-2', className)
    }, props));
}
function SidebarGroupLabel(_0) {
    let { className, asChild = false } = _0, props = _object_without_properties(_0, [
        "className",
        "asChild"
    ]);
    const Comp = asChild ? Slot : 'div';
    return /*#__PURE__*/ _jsx(Comp, _object_spread({
        "data-slot": "sidebar-group-label",
        "data-sidebar": "group-label",
        className: cn('text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', 'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0', className)
    }, props));
}
function SidebarGroupAction(_0) {
    let { className, asChild = false } = _0, props = _object_without_properties(_0, [
        "className",
        "asChild"
    ]);
    const Comp = asChild ? Slot : 'button';
    return /*#__PURE__*/ _jsx(Comp, _object_spread({
        "data-slot": "sidebar-group-action",
        "data-sidebar": "group-action",
        className: cn('text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden', 'group-data-[collapsible=icon]:hidden', className)
    }, props));
}
function SidebarGroupContent(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        "data-slot": "sidebar-group-content",
        "data-sidebar": "group-content",
        className: cn('w-full text-sm', className)
    }, props));
}
function SidebarMenu(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("ul", _object_spread({
        "data-slot": "sidebar-menu",
        "data-sidebar": "menu",
        className: cn('flex w-full min-w-0 flex-col gap-1', className)
    }, props));
}
function SidebarMenuItem(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("li", _object_spread({
        "data-slot": "sidebar-menu-item",
        "data-sidebar": "menu-item",
        className: cn('group/menu-item relative', className)
    }, props));
}
const sidebarMenuButtonVariants = cva('peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0', {
    variants: {
        variant: {
            default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            outline: 'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]'
        },
        size: {
            default: 'h-8 text-sm',
            sm: 'h-7 text-xs',
            lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!'
        }
    },
    defaultVariants: {
        variant: 'default',
        size: 'default'
    }
});
function SidebarMenuButton(_0) {
    let { asChild = false, isActive = false, variant = 'default', size = 'default', tooltip, className } = _0, props = _object_without_properties(_0, [
        "asChild",
        "isActive",
        "variant",
        "size",
        "tooltip",
        "className"
    ]);
    const Comp = asChild ? Slot : 'button';
    const { isMobile, state } = useSidebar();
    const button = /*#__PURE__*/ _jsx(Comp, _object_spread({
        "data-slot": "sidebar-menu-button",
        "data-sidebar": "menu-button",
        "data-size": size,
        "data-active": isActive,
        className: cn(sidebarMenuButtonVariants({
            variant,
            size
        }), className)
    }, props));
    if (!tooltip) {
        return button;
    }
    if (typeof tooltip === 'string') {
        tooltip = {
            children: tooltip
        };
    }
    return /*#__PURE__*/ _jsxs(Tooltip, {
        children: [
            /*#__PURE__*/ _jsx(TooltipTrigger, {
                asChild: true,
                children: button
            }),
            /*#__PURE__*/ _jsx(TooltipContent, _object_spread({
                side: "right",
                align: "center",
                hidden: state !== 'collapsed' || isMobile
            }, tooltip))
        ]
    });
}
function SidebarMenuAction(_0) {
    let { className, asChild = false, showOnHover = false } = _0, props = _object_without_properties(_0, [
        "className",
        "asChild",
        "showOnHover"
    ]);
    const Comp = asChild ? Slot : 'button';
    return /*#__PURE__*/ _jsx(Comp, _object_spread({
        "data-slot": "sidebar-menu-action",
        "data-sidebar": "menu-action",
        className: cn('text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0', // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden', 'peer-data-[size=sm]/menu-button:top-1', 'peer-data-[size=default]/menu-button:top-1.5', 'peer-data-[size=lg]/menu-button:top-2.5', 'group-data-[collapsible=icon]:hidden', showOnHover && 'peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0', className)
    }, props));
}
function SidebarMenuBadge(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        "data-slot": "sidebar-menu-badge",
        "data-sidebar": "menu-badge",
        className: cn('text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none', 'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground', 'peer-data-[size=sm]/menu-button:top-1', 'peer-data-[size=default]/menu-button:top-1.5', 'peer-data-[size=lg]/menu-button:top-2.5', 'group-data-[collapsible=icon]:hidden', className)
    }, props));
}
function SidebarMenuSkeleton(_0) {
    let { className, showIcon = false } = _0, props = _object_without_properties(_0, [
        "className",
        "showIcon"
    ]);
    // Random width between 50 to 90%.
    const width = React.useMemo(()=>{
        return "".concat((Math.floor(Math.random() * 40) + 50).toString(), "%");
    }, []);
    return /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({
        "data-slot": "sidebar-menu-skeleton",
        "data-sidebar": "menu-skeleton",
        className: cn('flex h-8 items-center gap-2 rounded-md px-2', className)
    }, props), {
        children: [
            showIcon && /*#__PURE__*/ _jsx(Skeleton, {
                className: "size-4 rounded-md",
                "data-sidebar": "menu-skeleton-icon"
            }),
            /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-4 max-w-(--skeleton-width) flex-1",
                "data-sidebar": "menu-skeleton-text",
                style: {
                    '--skeleton-width': width
                }
            })
        ]
    }));
}
function SidebarMenuSub(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("ul", _object_spread({
        "data-slot": "sidebar-menu-sub",
        "data-sidebar": "menu-sub",
        className: cn('border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-2.5 py-0.5', 'group-data-[collapsible=icon]:hidden', className)
    }, props));
}
function SidebarMenuSubItem(_0) {
    let { className } = _0, props = _object_without_properties(_0, [
        "className"
    ]);
    return /*#__PURE__*/ _jsx("li", _object_spread({
        "data-slot": "sidebar-menu-sub-item",
        "data-sidebar": "menu-sub-item",
        className: cn('group/menu-sub-item relative', className)
    }, props));
}
function SidebarMenuSubButton(_0) {
    let { asChild = false, size = 'md', isActive = false, className } = _0, props = _object_without_properties(_0, [
        "asChild",
        "size",
        "isActive",
        "className"
    ]);
    const Comp = asChild ? Slot : 'a';
    return /*#__PURE__*/ _jsx(Comp, _object_spread({
        "data-slot": "sidebar-menu-sub-button",
        "data-sidebar": "menu-sub-button",
        "data-size": size,
        "data-active": isActive,
        className: cn('text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0', 'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground', size === 'sm' && 'text-xs', size === 'md' && 'text-sm', 'group-data-[collapsible=icon]:hidden', className)
    }, props));
}
export { Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupAction, SidebarGroupContent, SidebarGroupLabel, SidebarHeader, SidebarInput, SidebarInset, SidebarMenu, SidebarMenuAction, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarMenuSkeleton, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem, SidebarProvider, SidebarRail, SidebarSeparator, SidebarTrigger, useSidebar };
